/**
 * Shared `HttpParams` builder for the Angular 18 API services.
 *
 * Replaces the legacy `QueryStringBuilderFactory` (b2b-ui), which iterated a
 * `{ requires, optional }` field spec and built a `?a=1&b=2` querystring. Here
 * we just take a plain object and (optionally) a whitelist of allowed keys.
 *
 * Rules
 *  - `null` and `undefined` values are skipped (parity with the legacy factory).
 *  - Empty-string values are skipped too — the legacy treated `''` as "no value".
 *  - If `fields` is supplied, keys not in the whitelist are skipped silently
 *    (mirrors the legacy `optional`/`requires` behaviour).
 *  - Field *required-ness* is intentionally NOT enforced at runtime; TypeScript
 *    catches missing required params at compile time via the service's own
 *    method signatures.
 */
import { HttpParams } from "@angular/common/http";

/**
 * Universal pagination + sort whitelist. Almost every list endpoint in the
 * legacy `QueryStringBuilderFactory` calls accepts these four fields. Spread
 * into per-endpoint whitelists:
 *
 *   const FORM_LIST_FIELDS = [...PAGINATION_FIELDS, 'category'] as const;
 */
export const PAGINATION_FIELDS = [
  "page",
  "pageSize",
  "sortField",
  "sortDirection",
] as const;

/**
 * Most list endpoints also accept `search`. Use this when the legacy whitelist
 * contained `search`; otherwise use `PAGINATION_FIELDS` so the per-endpoint
 * contract stays accurate (some legacy endpoints — e.g. resources schedule
 * summary — accept pagination but NOT search).
 */
export const PAGINATION_SEARCH_FIELDS = [
  ...PAGINATION_FIELDS,
  "search",
] as const;

export function buildParams(
  props: Record<string, unknown> | null | undefined,
  fields?: readonly string[],
): HttpParams {
  let params = new HttpParams();
  if (props == null) return params;
  const allowed = fields ? new Set<string>(fields) : null;
  for (const [key, value] of Object.entries(props)) {
    if (allowed && !allowed.has(key)) continue;
    if (value == null || value === "") continue;
    params = params.set(key, String(value));
  }
  return params;
}
